const enum TransactionType {
  DEPOSIT = 'deposit',
  WITHDRAWAL = 'withdrawal',
}

// eslint-disable-next-line no-restricted-syntax
enum PaymentMethod {
  AEROPAY = 'AEROPAY',
  CARD = 'CARD',
  PAYPAL = 'PAYPAL',
  ONLINE_BANKING = 'ONLINE_BANKING',
  PAPER_CHECK = 'PAPER_CHECK',
  VENMO = 'VENMO',
  SKRILL = 'SKRILL',
  WHOP = 'WHOP',
  WIRE = 'WIRE',
  EDGE_BOOST = 'EDGE_BOOST',
  WHOP_CRYPTO = 'WHOP_CRYPTO',
}

// eslint-disable-next-line no-restricted-syntax
enum PaymentType {
  SKRILL = 'SKRILL',
  AEROPAY = 'AEROPAY',
  CARD = 'CARD',
  PAYPAL = 'PAYPAL',
  PAPER_CHECK = 'PAPER_CHECK',
  VENMO = 'VENMO',
  VIPPREFERRED = 'VIPPREFERRED',
  WHOP = 'WHOP',
  WIRE = 'WIRE',
  EDGE_BOOST = 'EDGE_BOOST',
  WHOP_CRYPTO = 'WHOP_CRYPTO',
}

// eslint-disable-next-line no-restricted-syntax
enum AeropayBankName {
  AEROSYNC = 'Aerosync Bank',
  AEROSYNC_MFA = 'Aerosync Bank (MFA)',
  AEROSYNC_MFA_TRIGGER = 'Aerosync Bank (MFA Trigger)',
  AEROSYNC_OAUTH = 'Aerosync Bank (oAuth)',
  BANKOFAMERICA = 'Bank of America',
  BANKOFAMERICA_OAUTH = 'Bank of America Oauth',
  CAPITALONE = 'Capital One',
  CAPITALONE_INVESTMENTS = 'Capital One Investments, LLC',
  CHARLESSCHWAB = 'Charles Schwab',
  CHASE = 'Chase',
  CITI = 'Citibank',
  USAA = 'USAA',
  WELLSFARGO = 'Wells Fargo',
}

// eslint-disable-next-line no-restricted-syntax
enum AeropayBankInstitution {
  AEROSYNC = 'AEROSYNC',
  AEROSYNC_MFA = 'AEROSYNC_MFA',
  AEROSYNC_MFA_TRIGGER = 'AEROSYNC_MFA_TRIGGER',
  AEROSYNC_OAUTH = 'AEROSYNC_OAUTH',
  BANKOFAMERICA = 'BANKOFAMERICA',
  BANKOFAMERICA_OAUTH = 'BANKOFAMERICA_OAUTH',
  CAPITALONE = 'CAPITALONE',
  CAPITALONE_INVESTMENTS = 'CAPITALONE_INVESTMENTS',
  CHARLESSCHWAB = 'CHARLESSCHWAB',
  CHASE = 'CHASE',
  CITI = 'CITI',
  USAA = 'USAA',
  WELLSFARGO = 'WELLSFARGO',
}

const AEROPAY_BANK_NAME_OPTION = {
  [AeropayBankName.AEROSYNC]: AeropayBankInstitution.AEROSYNC,
  [AeropayBankName.AEROSYNC_MFA]: AeropayBankInstitution.AEROSYNC,
  [AeropayBankName.AEROSYNC_MFA_TRIGGER]: AeropayBankInstitution.AEROSYNC,
  [AeropayBankName.AEROSYNC_OAUTH]: AeropayBankInstitution.AEROSYNC,
  [AeropayBankName.BANKOFAMERICA]: AeropayBankInstitution.BANKOFAMERICA,
  [AeropayBankName.BANKOFAMERICA_OAUTH]: AeropayBankInstitution.BANKOFAMERICA,
  [AeropayBankName.CAPITALONE]: AeropayBankInstitution.CAPITALONE,
  [AeropayBankName.CAPITALONE_INVESTMENTS]: AeropayBankInstitution.CAPITALONE,
  [AeropayBankName.CHARLESSCHWAB]: AeropayBankInstitution.CHARLESSCHWAB,
  [AeropayBankName.CHASE]: AeropayBankInstitution.CHASE,
  [AeropayBankName.CITI]: AeropayBankInstitution.CITI,
  [AeropayBankName.USAA]: AeropayBankInstitution.USAA,
  [AeropayBankName.WELLSFARGO]: AeropayBankInstitution.WELLSFARGO,
};

const PAYMENT_TYPE_TO_METHOD = {
  [PaymentType.AEROPAY]: PaymentMethod.AEROPAY,
  [PaymentType.SKRILL]: PaymentMethod.SKRILL,
  [PaymentType.CARD]: PaymentMethod.CARD,
  [PaymentType.PAYPAL]: PaymentMethod.PAYPAL,
  [PaymentType.VIPPREFERRED]: PaymentMethod.ONLINE_BANKING,
  [PaymentType.VENMO]: PaymentMethod.VENMO,
  [PaymentType.PAPER_CHECK]: PaymentMethod.PAPER_CHECK,
  [PaymentType.WHOP]: PaymentMethod.WHOP,
  [PaymentType.WIRE]: PaymentMethod.WIRE,
  [PaymentType.EDGE_BOOST]: PaymentMethod.EDGE_BOOST,
  [PaymentType.WHOP_CRYPTO]: PaymentMethod.WHOP_CRYPTO,
} as const satisfies Record<PaymentType, PaymentMethod>;

/**
 * Payment statuses emitted by payments-service.
 *
 * Mirrors `PaymentStatus` in payments-service `wallet/src/entities/enums/transaction.enums.ts`
 * — the service that actually writes the column. The client-api-gateway keeps a narrower
 * copy omitting RECEIVED and CANCELLED, and payments-common documents the underlying column
 * as free text whose per-service enum copies have already drifted. Treat these as the known
 * values, not an exhaustive guarantee, and classify unknown values as non-terminal.
 */
const enum PaymentStatus {
  INITIATED = 'INITIATED',
  COMPLETED = 'COMPLETED',
  PAYABLE = 'PAYABLE',
  PROCESSING = 'PROCESSING',
  PENDING = 'PENDING',
  RECEIVED = 'RECEIVED',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  ERROR = 'ERROR',
  CANCELLED = 'CANCELLED',
}

/** String-union form of {@link PaymentStatus}, for typing API payload fields. */
type PaymentStatusValue = `${PaymentStatus}`;

const TERMINAL_SUCCESS_STATUSES = new Set<string>([PaymentStatus.COMPLETED]);
const TERMINAL_FAILURE_STATUSES = new Set<string>([
  PaymentStatus.FAILED,
  PaymentStatus.ERROR,
  PaymentStatus.EXPIRED,
  PaymentStatus.CANCELLED,
]);
const TERMINAL_STATUSES = new Set<string>([
  ...TERMINAL_SUCCESS_STATUSES,
  ...TERMINAL_FAILURE_STATUSES,
]);

const enum FeeType {
  AMOUNT = 'AMOUNT',
  PERCENTAGE = 'PERCENTAGE',
}

export type { PaymentStatusValue };

export {
  FeeType,
  PAYMENT_TYPE_TO_METHOD,
  PaymentMethod,
  PaymentStatus,
  PaymentType,
  TERMINAL_FAILURE_STATUSES,
  TERMINAL_STATUSES,
  TERMINAL_SUCCESS_STATUSES,
  TransactionType,
  AeropayBankInstitution,
  AEROPAY_BANK_NAME_OPTION,
  AeropayBankName,
};
